import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import { Outlet, json, redirect, useLoaderData } from '@remix-run/react'
import { PromoBarLatest } from '~/components/PromoBarLatest'
import AFFLogo from '~/images/logos/australian-frequent-flyer'
import PointHacksTravel from '~/images/logos/point-hacks-travel'
import { fetchAllStaticContent } from '~/models/content.server'
import { fetchReportLayout } from '~/models/report.v2.server'
import { commitSession, getSession } from '~/models/sessions.server'

export type RedemptionOption = Awaited<
  ReturnType<typeof fetchReportLayout>
>['redemptionOptions'][number]

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    {
      title: `${data?.report.report.brand} Concierge Report`,
    },
    { name: 'robots', content: 'noindex' },
  ]
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const [reportResult, contentResult] = await Promise.allSettled([
    fetchReportLayout(params.id!),
    fetchAllStaticContent(),
  ])

  if (reportResult.status === 'rejected') {
    console.error(reportResult.reason)
    throw new Response('Error fetching report', { status: 500 })
  }

  if (contentResult.status === 'rejected') {
    console.error(contentResult.reason)
    throw new Response('Error fetching report', { status: 500 })
  }

  const heroImage = contentResult.value
    .find((c) => c.id === '680e06ff-fc71-484e-a837-711cede422d8')
    ?.images.at(0)

  const promoBarText =
    contentResult.value.find((c) => c.id === 'clxles3yg000bvdn1jfv2zgyh')
      ?.shortText ?? 'Customise or build your own itinerary'

  const session = await getSession(request.headers.get('Cookie'))
  const hasAcknowledged = session.get('acknowledgeQuickBooking')

  return json(
    {
      hasAcknowledged,
      report: reportResult.value,
      heroImage,
      promoBarText,
    },
    { headers: { 'Set-Cookie': await commitSession(session) } },
  )
}

export async function action({ params, request }: ActionFunctionArgs) {
  const session = await getSession(request.headers.get('Cookie'))

  // Redirect to the user page
  session.set('acknowledgeQuickBooking', true)

  return redirect(request.url, {
    headers: {
      'Set-Cookie': await commitSession(session),
    },
  })
}

export default function ReportLayout() {
  const { report, heroImage } = useLoaderData<typeof loader>()

  return (
    <div className="bg-neutral-100">
      <header>
        <PromoBarLatest />
        {report.report.brand === 'Point Hacks' ? <PointHacksTravel /> : null}
        {report.report.brand === 'Frequent Flyer' ? <AFFLogo /> : null}
        {heroImage ? (
          <img
            width={heroImage.upload_width?.toString()}
            height={heroImage.upload_height?.toString()}
            className="w-full lg:max-h-[400px] min-h-[300px] object-cover"
            src={`https://concierge-cms.syd1.cdn.digitaloceanspaces.com/${heroImage.upload_id}.jpg`}
            alt={heroImage.caption ?? undefined}
          />
        ) : null}
      </header>
      <main
        className="relative bg-white -mt-[10vh] sm:mb-96 mx-auto max-w-5xl p-8 rounded-4xl
        prose prose-zinc prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg"
      >
        <Outlet />
      </main>
    </div>
  )
}
